import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import axios from 'axios';
import { v4 as uuid } from 'uuid';

import { GoForwardButton } from '../../components/HirePlanForm/ServidorGDF/GoForwardButton';
import { CartIcon } from '../../components/icons/CartIcon';
import Loading from '../../components/icons/Loading';
import XIcon from '../../components/icons/XIcon';
import { useWebSocketAssinatura } from '../../hooks/useWebSocketAssinatura';

interface Solicitacao {
  id: string;
  status: string;
  titularData: any;
}

const ConfirmacaoScreen: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { aguardarAssinatura } = useWebSocketAssinatura();

  const [solicitacao, setSolicitacao] = useState<Solicitacao | null>(null);
  const [loading, setLoading] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [iframe, setIframe] = useState<string | null>(null);
  const flowId = uuid();
  const [telaAssinatura, setTelaAssinatura] = useState(false);

  const urlBaseApiV2 = process.env.REACT_APP_OG_API_URL;

  const formatarCPF = (cpf: string | null | undefined): string => {
    if (!cpf) return '';
    const numerosCPF = cpf.toString().replace(/\D/g, '');
    if (numerosCPF.length !== 11) return cpf.toString();
    return numerosCPF.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  };

  const formatarCEP = (cep: string | null | undefined): string => {
    if (!cep) return '';
    const numerosCEP = cep.toString().replace(/\D/g, '');
    if (numerosCEP.length !== 8) return cep.toString();
    return numerosCEP.replace(/(\d{5})(\d{3})/, '$1-$2');
  };

  const formatarData = (dataString: string) => {
    try {
      const data = new Date(dataString);
      if (isNaN(data.getTime())) {
        return dataString;
      }
      const dia = String(data.getDate()).padStart(2, '0');
      const mes = String(data.getMonth() + 1).padStart(2, '0');
      const ano = data.getFullYear();
      return `${dia}/${mes}/${ano}`;
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return dataString;
    }
  };

  useEffect(() => {
    const fetchSolicitacao = async () => {
      try {
        const { data } = await axios.get(
          `${urlBaseApiV2}/ws/confirmar?id=${id}`,
        );
        setSolicitacao(data.retorno);
      } catch {
        setError('Erro ao carregar solicitação.');
      } finally {
        setLoading(false);
      }
    };

    fetchSolicitacao();
  }, [id]);

  const handleConfirmar = async (status: number) => {
    const browserInfo = {
      userAgent: navigator.userAgent,
      platform: navigator.platform,
      language: navigator.language,
    };

    const ipResponse = await fetch('https://api64.ipify.org?format=json');
    const { ip } = await ipResponse.json();

    const payload = {
      sender: flowId,
      id,
      status,
      browser_info: browserInfo,
      ip,
    };

    const { data } = await axios.post(`${urlBaseApiV2}/ws/confirmar`, payload);

    return data;
  };

  const handleConfirmarClick = async () => {
    try {
      setEnviando(true);

      const response = await handleConfirmar(2);

      if (!response?.AssinaturaHash?.Iframe) {
        throw new Error('Dados de assinatura inválidos');
      }
      setIframe(response.AssinaturaHash.Iframe);
      setTelaAssinatura(true);
      await aguardarAssinatura({
        flowId: flowId,
        onConfirm: async response => {
          alert('Solicitação confirmada com sucesso!');
          navigate('/proposta', {
            state: response.retorno?.pagamento,
          });
        },
      });
    } catch (err: any) {
      alert(err?.message || 'Erro ao confirmar solicitação');
      setEnviando(false);
    }
  };

  const handleCancelarClick = async () => {
    try {
      await handleConfirmar(3);
      alert('Processo cancelado com sucesso!');
    } catch {
      setError('Erro ao cancelar solicitação.');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p>Carregando...</p>
      </div>
    );
  }

  if (error || !solicitacao) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-red-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-4 md:px-14 md:pb-14 flex flex-col items-center h-full">
      {!telaAssinatura ? (
        <>
          <div className="bg-gray-50 p-5 rounded-lg mb-5 w-full md:w-4/5">
            <p>
              <strong>Solicitação:</strong> {id}
            </p>
            <p>
              <strong>Status:</strong> {solicitacao.status}
            </p>
          </div>
          <div className="bg-gray-50 p-5 rounded-lg mb-5 w-full md:w-4/5">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Dados do Titular
            </h2>
            <div className="space-y-3">
              <div className="flex flex-col md:flex-row border-b border-gray-200 pb-3">
                <span className="font-bold md:w-1/3">Nome do titular</span>
                <span className="md:w-2/3">{solicitacao.titularData.nome}</span>
              </div>
              <div className="flex flex-col md:flex-row border-b border-gray-200 pb-3">
                <span className="font-bold md:w-1/3">CPF</span>
                <span className="md:w-2/3">
                  {formatarCPF(solicitacao.titularData.cpf)}
                </span>
              </div>
              <div className="flex flex-col md:flex-row border-b border-gray-200 pb-3">
                <span className="font-bold md:w-1/3">Data de nascimento</span>
                <span className="md:w-2/3">
                  {formatarData(solicitacao.titularData.nascimento)}
                </span>
              </div>
              <div className="flex flex-col md:flex-row border-b border-gray-200 pb-3">
                <span className="font-bold md:w-1/3">Nome da mãe</span>
                <span className="md:w-2/3">{solicitacao.titularData.mae}</span>
              </div>
              <div className="flex flex-col md:flex-row border-b border-gray-200 pb-3">
                <span className="font-bold md:w-1/3">CEP</span>
                <span className="md:w-2/3">
                  {formatarCEP(solicitacao.titularData.cep)}
                </span>
              </div>
              <div className="flex flex-col md:flex-row border-b border-gray-200 pb-3">
                <span className="font-bold md:w-1/3">Endereço</span>
                <span className="md:w-2/3">
                  {solicitacao.titularData.endereco}
                </span>
              </div>
              <div className="flex flex-col md:flex-row border-b border-gray-200 pb-3">
                <span className="font-bold md:w-1/3">Complemento</span>
                <span className="md:w-2/3">
                  {solicitacao.titularData.complemento}
                </span>
              </div>
              <div className="flex flex-col md:flex-row">
                <span className="font-bold md:w-1/3">Produto Selecionado</span>
                <span className="md:w-2/3">
                  {solicitacao.titularData.produtoComercial}
                </span>
              </div>
              <div className="flex flex-col md:flex-row">
                <span className="font-bold md:w-1/3">Valor Total</span>
                <span className="md:w-2/3">
                  {solicitacao.titularData.valorTotal}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-5 rounded-lg mb-5 w-full md:w-4/5">
            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Dependentes
            </h2>
            {!Array.isArray(solicitacao.titularData.dependentes) ||
            solicitacao.titularData.dependentes.length == 0 ? (
              <p className="text-center text-gray-600">
                Nenhum dependente cadastrado.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left p-3">Nome</th>
                      <th className="p-3 hidden sm:table-cell">CPF</th>
                      <th className="p-3 hidden md:table-cell">Data Nasc.</th>
                      <th className="p-3 hidden lg:table-cell">Parentesco</th>
                      <th className="p-3 hidden lg:table-cell">Plano</th>
                    </tr>
                  </thead>
                  <tbody>
                    {solicitacao.titularData.dependentes.map(
                      (dependente, index) => (
                        <tr
                          key={index}
                          className="border-b border-gray-200 text-sm"
                        >
                          <td className="p-3">{dependente.nome}</td>
                          <td className="p-3 hidden sm:table-cell">
                            {formatarCPF(dependente.cpf)}
                          </td>
                          <td className="p-3 hidden md:table-cell">
                            {formatarData(dependente.nascimento)}
                          </td>
                          <td className="p-3 hidden lg:table-cell">
                            {dependente.parentesco}
                          </td>
                          <td className="p-3 hidden lg:table-cell">
                            {dependente.plano}
                          </td>
                        </tr>
                      ),
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="flex gap-4">
            <GoForwardButton
              onForwardClick={handleCancelarClick}
              Icon={XIcon}
              desktopText={'Cancelar'}
              mobileText={'Cancelar'}
              className="bg-warning-erro text-white px-4 py-2 rounded-md hover:bg-destructive-dark transition-colors w-full sm:w-auto"
            />

            <GoForwardButton
              disabled={enviando}
              Icon={enviando ? Loading : CartIcon}
              desktopText={enviando ? '' : 'Confirmar'}
              mobileText={enviando ? '' : 'Confirmar'}
              onForwardClick={handleConfirmarClick}
            />
          </div>
        </>
      ) : (
        <div className="w-full mt-10 h-full">
          <h2 className="text-2xl font-semibold text-center mb-6">
            Assinatura Digital
          </h2>

          {iframe ? (
            <iframe
              src={iframe}
              title="Assinatura"
              className="w-full min-h-[600px] border rounded-lg"
            />
          ) : (
            <p className="text-center text-red-500">
              Erro ao carregar assinatura
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default ConfirmacaoScreen;
